/**
 * Bundle laden und Bestand sichern.
 *
 * Der Import ersetzt nichts mehr: Seit in der App erfasst wird, waere das der
 * sichere Weg, eigene Arbeit zu verlieren. Stattdessen zwei Betriebsarten und
 * eine Vorschau, die vor dem Schreiben zeigt, was geschaehe.
 */

import { baueAbzug, dateiname } from '../abzug.js';
import { alleTauchgaenge } from '../db.js';
import { leseDateien } from '../garmin-import.js';
import { entpacke, planeImport, pruefeBundle, trenne, uebernimm } from '../laden.js';
import { grabsteine } from '../saetze.js';
import { geraeteKennung } from '../sync.js';

export function ansichtImport(db, aufFertig) {
  const wurzel = document.createElement('section');
  wurzel.className = 'import';
  wurzel.innerHTML = `
    <h1>Logbuch laden</h1>
    <p class="fliess">Wähle die Datei <code>logbook.json.gz</code> aus
      <code>out/export/</code> oder einen früheren Abzug. Die Daten bleiben auf
      diesem Gerät.</p>
    <fieldset class="betriebsart">
      <legend>Was der Import tun soll</legend>
      <label><input type="radio" name="modus" value="ergaenzen" checked>
        <span><strong>Ergänzen</strong> — nur anlegen, was fehlt. Deine
        Änderungen bleiben unangetastet.</span></label>
      <label><input type="radio" name="modus" value="abgleichen">
        <span><strong>Abgleichen</strong> — zusätzlich ersetzen, wo die Datei
        neuer ist als dein Stand.</span></label>
    </fieldset>
    <label class="dateiwahl">
      <input type="file" accept=".gz,.json" hidden>
      <span class="knopf">Datei auswählen</span>
    </label>
    <p class="fortschritt" role="status" aria-live="polite"></p>
    <hr>
    <h2>Garmin-Dateien einlesen</h2>
    <p class="fliess">Die Dateien <code>Dive-ACTIVITY*.json</code> aus dem
      Garmin-Export. Bekannte Tauchgänge werden übersprungen, Tiefenprofile
      kommen weiterhin über die Pipeline.</p>
    <label class="dateiwahl">
      <input type="file" class="garmin-wahl" accept=".json" multiple hidden>
      <span class="knopf">Dateien auswählen</span>
    </label>
    <p class="garmin-fortschritt" role="status" aria-live="polite"></p>
    <div class="garmin-bericht"></div>
    <hr>
    <h2>Sicherung</h2>
    <p class="fliess">Ist ein Cloud-Ordner verbunden, wandert alles Erfasste
      dorthin — eine halbe Minute nach der letzten Änderung, einzurichten unter
      <a href="#/sync">Sync</a>. Die Tiefenprofile bleiben dabei draussen; sie
      kommen über das Bundle oben.</p>
    <p class="fliess">Der Abzug ist der Notausgang daneben: Er enthält alles,
      was in dieser App liegt, <strong>samt Profilen</strong>, und braucht weder
      Konto noch Netz. Bewahre ihn ausserhalb des Browsers auf.</p>
    <button type="button" class="knopf abzug">Abzug herunterladen</button>`;

  const eingabe = wurzel.querySelector('input[type=file]');
  const fortschritt = wurzel.querySelector('.fortschritt');
  const knopf = wurzel.querySelector('.dateiwahl .knopf');

  eingabe.addEventListener('change', async () => {
    const datei = eingabe.files?.[0];
    if (!datei) return;
    const modus = wurzel.querySelector('input[name=modus]:checked').value;
    knopf.textContent = 'Liest …';
    eingabe.disabled = true;
    fortschritt.classList.remove('fehler');
    try {
      const daten = JSON.parse(await entpacke(datei));
      pruefeBundle(daten);
      const { kopfdaten } = trenne(daten.dives);
      const plan = planeImport(kopfdaten, await alleTauchgaenge(db),
        await grabsteine(db), modus);

      const zeilen = [`${plan.neu.length} neu`];
      if (plan.ersetzt.length) zeilen.push(`${plan.ersetzt.length} würden ersetzt`);
      if (plan.unveraendert.length) zeilen.push(`${plan.unveraendert.length} unverändert`);
      if (plan.uebersprungen.length) {
        zeilen.push(`${plan.uebersprungen.length} übersprungen (gelöscht)`);
      }
      // Bei 995 Tauchgängen will man vorher wissen, was ersetzt würde.
      if (!globalThis.confirm(`${zeilen.join(', ')}. Fortfahren?`)) {
        fortschritt.textContent = 'Abgebrochen, nichts geändert.';
        return;
      }

      const stand = await uebernimm(db, daten, (text) => { fortschritt.textContent = text; },
        { modus, geraet: geraeteKennung() });
      fortschritt.textContent = `${stand.tauchgaenge} Tauchgänge, ${stand.profile} Profile, `
        + `${stand.messpunkte.toLocaleString('de-CH')} Messpunkte übernommen.`;
      aufFertig();
    } catch (fehler) {
      fortschritt.textContent = `Nicht geladen: ${fehler.message}`;
      fortschritt.classList.add('fehler');
    } finally {
      eingabe.disabled = false;
      eingabe.value = '';
      knopf.textContent = 'Datei auswählen';
    }
  });

  const garminWahl = wurzel.querySelector('.garmin-wahl');
  const garminFortschritt = wurzel.querySelector('.garmin-fortschritt');
  const garminBericht = wurzel.querySelector('.garmin-bericht');

  garminWahl.addEventListener('change', async () => {
    const dateien = [...(garminWahl.files ?? [])];
    if (dateien.length === 0) return;
    garminWahl.disabled = true;
    garminFortschritt.classList.remove('fehler');
    garminBericht.replaceChildren();
    try {
      const bericht = await leseDateien(db, dateien, {
        geraet: geraeteKennung(),
        melde: (fertig, gesamt) => {
          garminFortschritt.textContent = `${fertig} von ${gesamt} …`;
        },
      });
      garminFortschritt.textContent = '';
      garminBericht.append(zeigeBericht(bericht));
      if (bericht.angelegt > 0) aufFertig();
    } catch (fehler) {
      garminFortschritt.textContent = `Abgebrochen: ${fehler.message}`;
      garminFortschritt.classList.add('fehler');
    } finally {
      garminWahl.disabled = false;
      garminWahl.value = '';
    }
  });

  wurzel.querySelector('.abzug').addEventListener('click', async () => {
    const daten = await baueAbzug(db);
    const blob = new Blob([JSON.stringify(daten)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const verweis = document.createElement('a');
    verweis.href = url;
    verweis.download = dateiname();
    verweis.click();
    URL.revokeObjectURL(url);
  });

  return wurzel;
}


/**
 * Was der Import getan hat — und was er ausgelassen hat.
 *
 * Der zweite Absatz steht fest: Der Verzicht auf CNS, N2 und die übrigen
 * Rechenwerte des Computers soll sichtbar sein und keine stille Lücke.
 */
function zeigeBericht(bericht) {
  const kasten = document.createElement('div');
  const zeilen = [`${bericht.angelegt} neu angelegt`];
  if (bericht.uebersprungen) zeilen.push(`${bericht.uebersprungen} bereits vorhanden`);
  if (bericht.ohneTauchgang) zeilen.push(`${bericht.ohneTauchgang} ohne Tauchgangsdaten`);
  if (bericht.unlesbar) zeilen.push(`${bericht.unlesbar} nicht lesbar`);

  const kopf = document.createElement('p');
  kopf.className = 'berichtzeile';
  kopf.textContent = zeilen.join(' · ');
  kasten.append(kopf);

  const zusage = document.createElement('p');
  zusage.className = 'fliess leise';
  zusage.textContent = 'Nicht übernommen werden: CNS, N2-Sättigung, '
    + 'O2-Toxizität, Kalorien und die Dekompressionseinstellungen. Wer sie '
    + 'braucht, liest die Dateien zusätzlich über die Pipeline ein.';
  kasten.append(zusage);

  if (bericht.hinweise.length) {
    const liste = document.createElement('ul');
    liste.className = 'hinweisliste';
    for (const hinweis of bericht.hinweise.slice(0, 10)) {
      const li = document.createElement('li');
      li.textContent = hinweis;
      liste.append(li);
    }
    kasten.append(liste);
  }
  return kasten;
}
